import { Card, CardBody, HStack, Stat, StatLabel, StatNumber, Skeleton, Text, Box, Tooltip } from '@chakra-ui/react'
import { Link as RouterLink } from 'react-router-dom'
import type { ReactNode } from 'react'

type Props = {
  label: string
  value: string | number
  loading?: boolean
  labelColor?: string
  valueColor?: string
  to?: string
  size?: 'sm' | 'md'
  /** Secondary line under the value (e.g. avg hold) */
  subtitle?: string | null
  subtitleColor?: string
  tooltip?: string
  icon?: ReactNode
  accent?: string
}

export default function DashboardStatCard({
  label,
  value,
  loading = false,
  labelColor = 'gray.600',
  valueColor,
  to,
  size = 'sm',
  subtitle,
  subtitleColor = 'orange.600',
  tooltip,
  icon,
  accent,
}: Props) {
  const card = (
    <Card
      bg="white"
      borderRadius="md"
      boxShadow="sm"
      h="100%"
      borderWidth="1px"
      borderColor="gray.100"
      borderLeftWidth={accent ? '3px' : '1px'}
      borderLeftColor={accent ?? 'gray.100'}
      _hover={to ? { boxShadow: 'md', borderColor: 'gray.200' } : undefined}
      as={to ? RouterLink : undefined}
      to={to}
      transition="box-shadow 0.15s ease"
    >
      <CardBody py={size === 'sm' ? 2.5 : 3} px={size === 'sm' ? 3 : 4}>
        <HStack align="flex-start" spacing={2}>
          {icon ? (
            <Box color={labelColor} mt={0.5} flexShrink={0} opacity={0.85}>
              {icon}
            </Box>
          ) : null}
          <Stat minW={0} flex="1">
            <StatLabel fontSize="xs" color={labelColor} fontWeight="medium" noOfLines={2} lineHeight="1.2">
              {label}
            </StatLabel>
            {loading ? (
              <Skeleton height={size === 'sm' ? '5' : '7'} mt={1} width="45%" />
            ) : (
              <>
                <StatNumber
                  fontSize={size === 'sm' ? 'lg' : 'xl'}
                  fontWeight="bold"
                  color={valueColor}
                  lineHeight="1.2"
                  mt={0.5}
                >
                  {typeof value === 'number' ? value.toLocaleString() : value}
                </StatNumber>
                {subtitle ? (
                  <Text fontSize="xs" color={subtitleColor} fontWeight="medium" mt={0.5} noOfLines={1}>
                    {subtitle}
                  </Text>
                ) : null}
              </>
            )}
          </Stat>
        </HStack>
      </CardBody>
    </Card>
  )

  if (tooltip) {
    return (
      <Tooltip label={tooltip} hasArrow placement="top">
        <Box h="100%">{card}</Box>
      </Tooltip>
    )
  }

  return card
}
